import { memo, useCallback, useMemo } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Fab from '@mui/material/Fab';
import Typography from '@mui/material/Typography';
import Zoom from '@mui/material/Zoom';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { useActiveHeading } from '../../hooks/useActiveHeading';
import type { Heading } from '../../types';

export interface DocumentNavigationProps {
  headings: Heading[];
  /** Restricts prev/next stepping to top-level sections. */
  maxLevel?: number;
}

const scrollToId = (id: string) => {
  const target = document.getElementById(id);
  target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  window.history.pushState(null, '', `#${id}`);
};

/** Previous/next section stepping plus a back-to-top control. */
export const DocumentNavigation = memo(function DocumentNavigation({
  headings,
  maxLevel = 2,
}: DocumentNavigationProps) {
  const sections = useMemo(
    () => headings.filter((heading) => heading.level <= maxLevel),
    [headings, maxLevel],
  );
  const activeId = useActiveHeading(headings);
  const trigger = useScrollTrigger({ disableHysteresis: true, threshold: 400 });

  const currentIndex = useMemo(() => {
    if (!activeId) return -1;
    const exact = sections.findIndex((section) => section.id === activeId);
    if (exact !== -1) return exact;

    // The active heading may be deeper than maxLevel; fall back to its parent section.
    const activePosition = headings.findIndex((heading) => heading.id === activeId);
    for (let index = activePosition; index >= 0; index -= 1) {
      const candidate = sections.findIndex((section) => section.id === headings[index]?.id);
      if (candidate !== -1) return candidate;
    }
    return -1;
  }, [activeId, headings, sections]);

  const previous = currentIndex > 0 ? sections[currentIndex - 1] : undefined;
  const next =
    currentIndex !== -1 && currentIndex < sections.length - 1 ? sections[currentIndex + 1] : undefined;

  const toTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.history.pushState(null, '', window.location.pathname + window.location.search);
  }, []);

  if (sections.length === 0) return null;

  return (
    <>
      <Box component="nav" aria-label="Section navigation" className="document-navigation" sx={{ mt: 6 }}>
        <Divider sx={{ mb: 3 }} />
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          }}
        >
          {previous ? (
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ArrowBackRoundedIcon />}
              onClick={() => scrollToId(previous.id)}
              sx={{ justifyContent: 'flex-start', textAlign: 'left', p: 1.5, borderRadius: 2 }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Previous
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {previous.text}
                </Typography>
              </Box>
            </Button>
          ) : (
            <Box />
          )}

          {next && (
            <Button
              variant="outlined"
              color="inherit"
              endIcon={<ArrowForwardRoundedIcon />}
              onClick={() => scrollToId(next.id)}
              sx={{
                justifyContent: 'flex-end',
                textAlign: 'right',
                p: 1.5,
                borderRadius: 2,
                gridColumn: { sm: 2 },
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Next
                </Typography>
                <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                  {next.text}
                </Typography>
              </Box>
            </Button>
          )}
        </Box>
      </Box>

      <Zoom in={trigger}>
        <Fab
          size="small"
          color="primary"
          aria-label="Back to top"
          onClick={toTop}
          className="back-to-top"
          sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (theme) => theme.zIndex.fab }}
        >
          <KeyboardArrowUpRoundedIcon />
        </Fab>
      </Zoom>
    </>
  );
});

export default DocumentNavigation;
